import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

export type ExerciseFeedback = 'correct' | 'incorrect' | null;

export interface QuantityQuestionView {
  readonly kind: 'quantity';
  readonly instruction: string;
  readonly symbol: string;
  readonly count: number;
  readonly choices: readonly number[];
}

export interface CompareQuestionView {
  readonly kind: 'compare';
  readonly instruction: string;
  readonly groups: readonly {
    readonly value: number;
    readonly count: number;
    readonly symbol: string;
    readonly label: string;
  }[];
}

export interface SequenceQuestionView {
  readonly kind: 'sequence';
  readonly instruction: string;
  readonly pattern: readonly string[];
  readonly layout: 'sequence' | 'arithmetic';
  readonly choices: readonly {
    readonly value: number;
    readonly visual: string;
    readonly label: string;
  }[];
}

export interface InstructionChoiceQuestionView {
  readonly kind: 'instruction-choice';
  readonly instruction: string;
  readonly choices: readonly {
    readonly value: number;
    readonly visual: string;
    readonly label: string;
  }[];
}

export interface TapOrderQuestionView {
  readonly kind: 'tap-order';
  readonly instruction: string;
}

export type ExerciseQuestionView =
  | QuantityQuestionView
  | CompareQuestionView
  | SequenceQuestionView
  | InstructionChoiceQuestionView
  | TapOrderQuestionView;

export interface TapOrderView {
  readonly tiles: readonly {
    readonly position: number;
    readonly visual: string;
    readonly label: string;
    readonly selected: boolean;
  }[];
  readonly selectedVisuals: readonly (string | null)[];
  readonly canReset: boolean;
}

export interface ExerciseScreenViewModel {
  readonly profileNickname: string;
  readonly domainLabel: string;
  readonly question: ExerciseQuestionView;
  readonly questionNumber: number;
  readonly questionCount: number;
  readonly progressPercent: number;
  readonly feedback: ExerciseFeedback;
  readonly busy: boolean;
  readonly canAdvance: boolean;
  readonly tapOrder: TapOrderView | null;
}

@Component({
  selector: 'app-exercise-screen',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './exercise-screen.component.scss',
  template: `
    @if (view(); as state) {
      <section class="exercise">
        <header class="exercise-header">
          <div class="exercise-identity">
            <span class="mini-avatar" aria-hidden="true">★</span>
            <div>
              <p class="eyebrow">{{ state.domainLabel }}</p>
              <strong>{{ state.profileNickname }}</strong>
            </div>
          </div>
          <button class="quiet-button compact-button" type="button" (click)="leaveActivity.emit()">Giochi</button>
        </header>

        <div
          class="progress"
          role="progressbar"
          aria-label="Avanzamento esercizio"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="state.progressPercent"
        >
          <span [style.width.%]="state.progressPercent"></span>
        </div>

        <section class="card child-card exercise-card">
          <div class="exercise-topline">
            <p class="step">{{ state.questionNumber }} di {{ state.questionCount }}</p>
            <button class="listen-button" type="button" aria-label="Ascolta la domanda" (click)="listen.emit()">
              <span aria-hidden="true">🔊</span>
            </button>
          </div>
          <h1 class="sr-only">{{ state.question.instruction }}</h1>

          @switch (state.question.kind) {
            @case ('quantity') {
              <div class="objects" aria-label="Oggetti da contare">
                @for (_ of range(state.question.count); track $index) {
                  <span aria-hidden="true">{{ state.question.symbol }}</span>
                }
              </div>
              <div class="answers" aria-label="Scegli il numero corretto">
                @for (choice of state.question.choices; track choice) {
                  <button
                    type="button"
                    [disabled]="state.busy || state.feedback === 'correct'"
                    (click)="answer.emit(choice)"
                  >{{ choice }}</button>
                }
              </div>
            }

            @case ('compare') {
              <div class="compare-groups" aria-label="Confronta i due gruppi">
                @for (group of state.question.groups; track group.value) {
                  <button
                    class="compare-group"
                    type="button"
                    [attr.aria-label]="group.label"
                    [disabled]="state.busy || state.feedback === 'correct'"
                    (click)="answer.emit(group.value)"
                  >
                    @for (_ of range(group.count); track $index) {
                      <span aria-hidden="true">{{ group.symbol }}</span>
                    }
                  </button>
                }
              </div>
            }

            @case ('sequence') {
              <div
                class="pattern-board"
                [class.arithmetic-board]="state.question.layout === 'arithmetic'"
                [attr.aria-label]="state.question.layout === 'arithmetic' ? 'Operazione da completare' : 'Sequenza da completare'"
              >
                @for (item of state.question.pattern; track $index) {
                  <span aria-hidden="true">{{ item }}</span>
                }
                <span class="pattern-question" aria-hidden="true">?</span>
              </div>
              <div class="answers visual-answers" aria-label="Scegli cosa viene dopo">
                @for (choice of state.question.choices; track choice.value) {
                  <button
                    type="button"
                    [attr.aria-label]="choice.label"
                    [disabled]="state.busy || state.feedback === 'correct'"
                    (click)="answer.emit(choice.value)"
                  >{{ choice.visual }}</button>
                }
              </div>
            }

            @case ('instruction-choice') {
              <div class="instruction-board" aria-hidden="true">
                <span>👂</span>
              </div>
              <div class="answers visual-answers instruction-answers" aria-label="Scegli l'oggetto richiesto">
                @for (choice of state.question.choices; track choice.value) {
                  <button
                    type="button"
                    [attr.aria-label]="choice.label"
                    [disabled]="state.busy || state.feedback === 'correct'"
                    (click)="answer.emit(choice.value)"
                  >{{ choice.visual }}</button>
                }
              </div>
            }

            @case ('tap-order') {
              @if (state.tapOrder; as order) {
                <div class="order-board">
                  <div class="order-slots" aria-label="Ordine scelto">
                    @for (selectedVisual of order.selectedVisuals; track $index) {
                      <div class="order-slot" [class.filled]="selectedVisual !== null">
                        <span class="order-step-number" aria-hidden="true">{{ $index + 1 }}</span>
                        @if (selectedVisual) {
                          <span class="order-slot-visual" aria-hidden="true">{{ selectedVisual }}</span>
                        } @else {
                          <span class="order-slot-placeholder" aria-hidden="true">?</span>
                        }
                      </div>
                      @if ($index === 0) {
                        <span class="order-arrow" aria-hidden="true">→</span>
                      }
                    }
                  </div>

                  <div class="order-tiles" aria-label="Tocca le immagini nell'ordine giusto">
                    @for (tile of order.tiles; track tile.position) {
                      <button
                        class="order-tile"
                        type="button"
                        [class.selected]="tile.selected"
                        [attr.aria-label]="tile.label"
                        [disabled]="state.busy || state.feedback === 'correct' || tile.selected"
                        (click)="selectOrderTile.emit(tile.position)"
                      >{{ tile.visual }}</button>
                    }
                  </div>

                  @if (order.canReset && state.feedback !== 'correct') {
                    <button class="quiet-button order-reset" type="button" [disabled]="state.busy" (click)="resetOrder.emit()">
                      <span aria-hidden="true">↻</span><span class="sr-only">Ricomincia l'ordine</span>
                    </button>
                  }
                </div>
              }
            }
          }

          <div class="feedback" aria-live="polite">
            @if (state.feedback === 'incorrect') {
              <span class="feedback-mark retry" aria-hidden="true">↻</span>
              <span class="sr-only">Prova ancora.</span>
            }
            @if (state.feedback === 'correct') {
              <span class="feedback-mark success" aria-hidden="true">✓</span>
              <span class="sr-only">Giusto.</span>
              <button
                class="next-button"
                type="button"
                aria-label="Vai avanti"
                [disabled]="!state.canAdvance"
                (click)="next.emit()"
              >
                <span aria-hidden="true">➜</span>
              </button>
            }
          </div>
        </section>
      </section>
    }
  `
})
export class ExerciseScreenComponent {
  readonly view = input.required<ExerciseScreenViewModel>();

  readonly leaveActivity = output<void>();
  readonly listen = output<void>();
  readonly answer = output<number>();
  readonly selectOrderTile = output<number>();
  readonly resetOrder = output<void>();
  readonly next = output<void>();

  protected range(count: number): readonly number[] {
    return Array.from({ length: count }, (_, index) => index);
  }
}
